import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MessageCircle, Sparkles } from 'lucide-react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../components/ui/TaraCard';
import TaraBadge from '../components/ui/TaraBadge';
import BottomNav from '../components/BottomNav';
import AskSageAnything from '../components/AskSageAnything';
import { agentPersonalities } from '../agents/agentPersonalities';
import DynamicSky from '../components/DynamicSky';

const AIAgentsHub = () => {
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  const hour = new Date().getHours();
  const timeOfDay = hour < 6 ? 'night' : hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 21 ? 'evening' : 'night';

  const agents = Object.values(agentPersonalities);

  return (
    <div className="min-h-screen bg-stone-50 pb-24 relative overflow-hidden">
      {/* Dynamic Sky Background */}
      <DynamicSky 
        timeOfDay={timeOfDay}
        planetaryInfluence="jupiter"
        dasha="venus"
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="px-5 py-8 bg-gradient-to-br from-cosmic-100 to-sacred-100">
          <div className="max-w-5xl mx-auto">
            <button 
              onClick={() => navigate('/home')}
              className="mb-4 p-2 hover:bg-white/50 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-stone-700" />
            </button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="text-5xl">🤖</div>
              <div>
                <h1 className="font-serif text-3xl md:text-4xl font-medium text-stone-900">
                  Your AI Agents
                </h1>
                <p className="text-sm text-stone-600 mt-2">
                  8 specialized intelligences, each an expert in their domain
                </p>
              </div>
            </div>

            {/* Key Features */}
            <div className="flex flex-wrap gap-2 mt-4">
              <TaraBadge variant="calm" size="sm">
                <Sparkles className="w-3 h-3 mr-1" />
                Personalized
              </TaraBadge>
              <TaraBadge variant="vibrant" size="sm">
                24/7 Available
              </TaraBadge>
              <TaraBadge variant="sacred" size="sm">
                Astro-Powered
              </TaraBadge>
            </div>
          </div>
        </div>

        {/* Agents Grid */}
        <div className="px-5 py-8 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agents.map((agent) => (
              <TaraCard 
                key={agent.id}
                interactive
                className="overflow-hidden"
                onClick={() => setSelectedAgent(agent.id)}
              >
                <div 
                  className="h-2 w-full"
                  style={{ backgroundColor: agent.color }}
                />
                <TaraCardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{agent.avatar}</div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl font-medium text-stone-900">
                        {agent.name}
                      </h3>
                      <p className="text-xs text-stone-600 mb-3">
                        {agent.title}
                      </p>
                      
                      {/* Personality Traits */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {agent.personality.traits.slice(0, 3).map((trait) => (
                          <span 
                            key={trait}
                            className="px-2 py-0.5 bg-stone-100 text-stone-700 text-xs rounded-full"
                          >
                            {trait}
                          </span>
                        ))}
                      </div>

                      {/* Expertise */}
                      <div className="mb-4">
                        <p className="text-xs text-stone-600 mb-1.5 font-medium">
                          Expert in:
                        </p>
                        <ul className="text-xs text-stone-600 space-y-0.5">
                          {agent.expertise.slice(0, 3).map((exp) => (
                            <li key={exp} className="flex items-center gap-1.5">
                              <div 
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: agent.color }}
                              />
                              {exp}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAgent(agent.id);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-button font-medium text-sm transition-all"
                        style={{ 
                          backgroundColor: `${agent.color}15`,
                          color: agent.color,
                        }}
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat with {agent.name}
                      </button>
                    </div>
                  </div>
                </TaraCardContent>
              </TaraCard>
            ))}
          </div>

          {/* Info Section */}
          <TaraCard className="mt-8" variant="cosmic">
            <TaraCardHeader>
              <TaraCardTitle>How AI Agents Work</TaraCardTitle>
            </TaraCardHeader>
            <TaraCardContent className="p-6">
              <div className="space-y-4 text-sm text-stone-700">
                <div className="flex items-start gap-3">
                  <div className="text-xl">🧠</div>
                  <div>
                    <p className="font-medium mb-1">Astrology-Powered Intelligence</p>
                    <p className="text-stone-600">
                      Each agent analyzes your birth chart, current transits, and dasha periods 
                      to provide personalized, time-sensitive guidance.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-xl">🎯</div>
                  <div>
                    <p className="font-medium mb-1">Domain-Specific Expertise</p>
                    <p className="text-stone-600">
                      Unlike generic AI, each agent is specialized in their field with deep 
                      knowledge of planetary influences and life patterns.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-xl">💡</div>
                  <div>
                    <p className="font-medium mb-1">Proactive Micro-Insights</p>
                    <p className="text-stone-600">
                      Agents don't just respond to questions - they proactively alert you to 
                      opportunities, risks, and optimal timing windows.
                    </p>
                  </div>
                </div>
              </div>
            </TaraCardContent>
          </TaraCard>
        </div>
      </div>

      {/* AI Agent Chat Modal */}
      {selectedAgent && (
        <AskSageAnything 
          domainId={selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}

      <BottomNav active="home" />
    </div>
  );
};

export default AIAgentsHub;
